import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api'
import { queryKeys } from '@/lib/query'
import type { DirectionTemplate, RoutePointKind } from '@/types/domain'

/* The direction catalogue — issue #35 story 1.
 *
 * A direction is a named route made of ordered points, each carrying a country
 * and a normative transit time from the point before it. Super-admin maintains
 * the catalogue; a later story fills a case leg from one.
 *
 * ── The read is NOT admin-only ─────────────────────────────────────────────
 * `GET /api/settings/directions` sits on the plain session group, and the leg
 * page's picker reads it. The writes sit under `/admin/settings`: create,
 * update and the active switch behind `route.template.manage` OR
 * `admin.settings.manage` (S-10 — Operations and Pricing create templates
 * from the leg page's «Сохранить как шаблон» and in Settings), the delete
 * behind `admin.settings.manage` alone. Nothing here re-checks a capability —
 * the route table is the guard (AD-5); the controls that call these are
 * gated through `lib/can.ts` as a courtesy.
 *
 * ── WHAT MUST NOT BE BUILT HERE ────────────────────────────────────────────
 * Applying a direction to a case leg COPIES its points onto that leg. Editing a
 * template must never move a shipment already in flight, so nothing in this
 * file may grow a mutation that reaches a case, and nothing may start sending a
 * template id anywhere near a leg. This is `useCompanyTemplates`' rule about
 * quotes, restated for a resource whose mistake would be more expensive: a
 * wrong offer is words, a wrong position is where a client believes their cargo
 * is.
 *
 * No `USE_MOCKS` branch, unlike the entity and numbering reads above it. The
 * fixtures in `src/mocks/` exist for offline UI work against the showcase
 * dataset, and this resource has no fixture — inventing one here would be a
 * second catalogue to keep in step by hand for a screen that has always had a
 * real endpoint.
 */

/** One point as the server accepts it. Positions are absent by design: the
 *  server allocates them from this slice's ORDER, so a removed point cannot
 *  leave a gap the client forgot to close. */
export interface DirectionPointPayload {
  name: string
  /** ISO 3166-1 alpha-2, or `''` for unset. */
  country: string
  kind: RoutePointKind
  /** `null` on the first point and only there — it has no inbound transition. */
  daysFromPrevious: number | null
}

/** The whole record, submitted whole.
 *
 * Not a patch, for the reason `useUpdateEntity` gives: a nil-means-unchanged
 * body makes "clear this" indistinguishable from "leave it alone" — and for a
 * point LIST there is no patch shape at all that expresses "these four, in this
 * order" without the client inventing positions the server owns. */
export interface DirectionPayload {
  name: string
  active: boolean
  points: DirectionPointPayload[]
}

/** The catalogue.
 *
 * `activeOnly` narrows to what a picker may offer. Settings reads the
 * unfiltered list, because a screen that maintains the catalogue must be able
 * to see and reactivate what it switched off — and so does the leg page's
 * name check before «Сохранить как шаблон» (S-10): a retired template still
 * owns its name (`directionNameTaken`). */
export function useDirectionTemplates(options?: { activeOnly?: boolean }) {
  const activeOnly = options?.activeOnly ?? false

  return useQuery<DirectionTemplate[]>({
    queryKey: queryKeys.settings.directions(activeOnly),
    queryFn: async () =>
      (
        await api.get<{ items: DirectionTemplate[] }>('/settings/directions', {
          params: activeOnly ? { active: 'true' } : undefined,
        })
      ).items,
  })
}

/** Invalidates the whole directions subtree.
 *
 * The PREFIX rather than one key: the settings list and any active-only picker
 * are two cached answers to one catalogue, and a mutation fired from the
 * settings screen has never seen the picker's narrowing. */
function useInvalidateDirections() {
  const queryClient = useQueryClient()
  return () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.settings.directionsAll })
  }
}

export function useCreateDirection() {
  const invalidate = useInvalidateDirections()

  return useMutation<DirectionTemplate, Error, DirectionPayload>({
    mutationFn: (payload) =>
      api.post<DirectionTemplate>('/admin/settings/directions', payload),
    onSuccess: invalidate,
  })
}

export function useUpdateDirection() {
  const invalidate = useInvalidateDirections()

  return useMutation<DirectionTemplate, Error, { id: string; payload: DirectionPayload }>({
    mutationFn: ({ id, payload }) =>
      api.put<DirectionTemplate>(`/admin/settings/directions/${id}`, payload),
    onSuccess: invalidate,
  })
}

/** Switches a direction on or off.
 *
 * Its own endpoint rather than a full update carrying `active`, so retiring a
 * corridor from a list row does not require the row to hold every point it
 * would otherwise have to send back unchanged. */
export function useSetDirectionActive() {
  const invalidate = useInvalidateDirections()

  return useMutation<DirectionTemplate, Error, { id: string; active: boolean }>({
    mutationFn: ({ id, active }) =>
      api.put<DirectionTemplate>(`/admin/settings/directions/${id}/active`, { active }),
    onSuccess: invalidate,
  })
}

/** Removes a direction outright.
 *
 * A hard delete, which is unusual here and worth saying why: every directory
 * record is deactivated instead, because historical rows reference it. A
 * direction has no such references BY DESIGN — applying one copies its points
 * onto the leg — so removing one takes it out of the picker and changes no case
 * that ever used it. Deactivation is still offered beside it, and is the right
 * answer for a corridor that may come back. */
export function useDeleteDirection() {
  const invalidate = useInvalidateDirections()

  return useMutation<void, Error, string>({
    mutationFn: (id) => api.delete<void>(`/admin/settings/directions/${id}`),
    onSuccess: invalidate,
  })
}
